import type {
  ChoiceOption,
  FhsField,
  FhsStepName,
  IconChoiceOption,
} from './types';

// Delay before auto-advancing to the next step after selecting a value.
export const AUTO_ADVANCE_MS = 320;

export const INPUT_THEME = {
  label: {
    text: {
      fontSize: 'var(--size-big)',
      fontWeight: 'var(--weight-semibold)',
      lineHeight: 'var(--line-height-big)',
      color: 'var(--text-default)',
    },
  },
  input: {
    element: {
      color: 'var(--text-default)',
    },
  },
};

// Steps that auto-advance on radio selection — the Continue button is hidden on
// these because picking an option already moves the user forward.
export const AUTO_ADVANCE_STEPS: ReadonlySet<FhsStepName> =
  new Set<FhsStepName>([
    'age',
    'maritalStatus',
    'financialDependents',
    'retirement',
  ]);

// Step keys (machine vocabulary) → analytics `action` values (GA vocabulary).
// Several diverge (income→savings, maritalStatus→marriage, etc.) because the
// analytics team owns the action names independently. Typed Partial so future
// states (or runtime drift) without an action mapping silently skip the
// event rather than dispatching `undefined`.
export const STEP_TO_ACTION: Partial<Record<FhsStepName, string>> = {
  name: 'name',
  age: 'age',
  maritalStatus: 'marriage',
  financialDependents: 'dependants',
  retirement: 'retirement',
  insurance: 'insurance',
  estatePlanning: 'estate',
  income: 'savings',
  investment: 'investments',
  realEstate: 'real_estate',
  debt: 'debt',
  monthlyExpenses: 'expenses',
  summary: 'pre_submit',
};

export const STEP_ORDER: FhsStepName[] = [
  'name',
  'age',
  'maritalStatus',
  'financialDependents',
  'retirement',
  'insurance',
  'estatePlanning',
  'income',
  'investment',
  'realEstate',
  'debt',
  'monthlyExpenses',
  'summary',
];

export const SECTIONS: {label: string; stepNames: FhsStepName[]}[] = [
  {
    label: 'About You',
    stepNames: ['name', 'age', 'maritalStatus', 'financialDependents'],
  },
  {
    label: 'Your Future',
    stepNames: ['retirement', 'insurance', 'estatePlanning'],
  },
  {
    label: 'Your Wealth',
    stepNames: [
      'income',
      'investment',
      'realEstate',
      'debt',
      'monthlyExpenses',
    ],
  },
];

export const STEP_TITLES: Record<
  FhsStepName,
  {title: string; subtitle?: string; note?: string}
> = {
  name: {title: "What's your name?", subtitle: 'Hey, welcome to Zoe.'},
  age: {title: 'How old are you?', subtitle: 'Nice to meet you.'},
  maritalStatus: {
    title: 'Are you married or in a domestic partnership?',
    subtitle: "You're in good hands.",
  },
  financialDependents: {
    title: 'Do you have children or financial dependents?',
    subtitle: 'Almost there on the basics.',
  },
  retirement: {
    title: 'When do you see yourself retiring?',
    subtitle: "Let's start with the big picture.",
  },
  insurance: {
    title: 'Which types of insurance do you currently have?',
    subtitle: "Now let's check your safety net.",
    note: 'Select all that apply.',
  },
  estatePlanning: {
    title: 'Which estate planning documents do you currently have in place?',
    subtitle: "Let's look at one more layer of protection.",
    note: 'Select all that apply.',
  },
  income: {
    title: "Let's start with what you earn and save.",
    subtitle: 'Estimates are totally fine here.',
  },
  investment: {
    title: 'Your investment picture',
    subtitle: "Now let's look at your investments.",
  },
  realEstate: {
    title: 'Do you own any real estate?',
    subtitle: 'One more asset to account for.',
  },
  debt: {
    title: 'Your debt picture',
    subtitle: 'Almost done. Last financial section.',
  },
  monthlyExpenses: {
    title: 'What are your total monthly living expenses?',
    subtitle: 'Last one.',
    note: 'Housing, food, transport, subscriptions, everything except debt payments.',
  },
  summary: {
    title: 'Here are your answers',
    subtitle: 'Review what you shared.',
  },
};

// Allowed-value tuples: source of truth for schema validation (z.enum(...)) on
// each choice-bound field. Keep the *_OPTIONS arrays in sync — if a label is
// added, also add its value here, otherwise the schema will reject it.
export const RETIREMENT_VALUES = [
  'before_55',
  'late_50s',
  'around_60_65',
  'after_65',
  'not_sure',
  'already_retired',
] as const;

export const AGE_VALUES = [
  'under_30s',
  '30s',
  '40s',
  '50s',
  '60s',
  '70_plus',
] as const;

export const INSURANCE_VALUES = [
  'life_insurance',
  'disability_insurance',
  'umbrella_insurance',
  'long_term_care_insurance',
  'NONE',
] as const;

export const MARITAL_VALUES = ['married', 'single'] as const;

export const REAL_ESTATE_OWNERSHIP_VALUES = ['yes', 'no'] as const;

export const FINANCIAL_DEPENDENTS_VALUES = ['yes', 'no'] as const;

export const ESTATE_VALUES = [
  'will',
  'trust',
  'poa',
  'healthcare',
  'beneficiaries',
  'NONE',
] as const;

export const RETIREMENT_OPTIONS: ChoiceOption[] = [
  {label: 'Before 55', value: 'before_55'},
  {label: 'In my late 50s', value: 'late_50s'},
  {label: 'Around 60-65', value: 'around_60_65'},
  {label: 'After 65', value: 'after_65'},
  {label: 'Not sure yet', value: 'not_sure'},
  {label: 'Already retired', value: 'already_retired'},
];

export const AGE_OPTIONS: ChoiceOption[] = [
  {label: 'Under 30s', value: 'under_30s'},
  {label: '30s', value: '30s'},
  {label: '40s', value: '40s'},
  {label: '50s', value: '50s'},
  {label: '60s', value: '60s'},
  {label: '70+', value: '70_plus'},
];

export const INSURANCE_OPTIONS: IconChoiceOption[] = [
  {
    label: 'Life Insurance',
    value: 'life_insurance',
    iconName: 'fa-heart-circle-bolt',
    iconWeight: 'fa-light',
  },
  {
    label: 'Disability Insurance',
    value: 'disability_insurance',
    iconName: 'fa-wheelchair-move',
    iconWeight: 'fa-light',
  },
  {
    label: 'Umbrella Insurance',
    value: 'umbrella_insurance',
    iconName: 'fa-umbrella',
    iconWeight: 'fa-light',
  },
  {
    label: 'Long-Term Care Insurance',
    value: 'long_term_care_insurance',
    iconName: 'fa-note-medical',
    iconWeight: 'fa-light',
  },
  {
    label: 'None of these',
    value: 'NONE',
    iconName: 'fa-empty-set',
    iconWeight: 'fa-light',
  },
];

export const MARITAL_OPTIONS: IconChoiceOption[] = [
  {
    label: 'Married / partnered',
    value: 'married',
    iconName: 'fa-people',
    iconWeight: 'fa-thin',
  },
  {
    label: 'Single / not partnered',
    value: 'single',
    iconName: 'fa-person',
    iconWeight: 'fa-thin',
  },
];

// Real estate ownership is a two-step question: pick yes/no first, then —
// only if yes — enter the value. The "no" path auto-advances; the "yes"
// path requires the follow-up amount before Continue activates.
export const REAL_ESTATE_OWNERSHIP_OPTIONS: IconChoiceOption[] = [
  {
    label: 'Yes, I do',
    value: 'yes',
    iconName: 'fa-circle-check',
    iconWeight: 'fa-light',
  },
  {
    label: "No, I don't",
    value: 'no',
    iconName: 'fa-square-xmark',
    iconWeight: 'fa-light',
  },
];

export const FINANCIAL_DEPENDENTS_OPTIONS: IconChoiceOption[] = [
  {
    label: 'Yes, I do',
    value: 'yes',
    iconName: 'fa-hands-holding-child',
    iconWeight: 'fa-light',
  },
  {
    label: 'No, just me',
    value: 'no',
    iconName: 'fa-house-chimney-heart',
    iconWeight: 'fa-light',
  },
];

export const ESTATE_OPTIONS: IconChoiceOption[] = [
  {
    label: 'Will',
    value: 'will',
    iconName: 'fa-file-contract',
    iconWeight: 'fa-solid',
  },
  {
    label: 'Trust',
    value: 'trust',
    iconName: 'fa-shield-halved',
    iconWeight: 'fa-solid',
  },
  {
    label: 'Power of Attorney',
    value: 'poa',
    iconName: 'fa-gavel',
    iconWeight: 'fa-solid',
  },
  {
    label: 'Healthcare Directive',
    value: 'healthcare',
    iconName: 'fa-heart-pulse',
    iconWeight: 'fa-solid',
  },
  {
    label: 'Beneficiaries reviewed',
    value: 'beneficiaries',
    iconName: 'fa-users',
    iconWeight: 'fa-solid',
  },
  {
    label: 'None of these',
    value: 'NONE',
    iconName: 'fa-xmark',
    iconWeight: 'fa-solid',
  },
];

// Maps each multi-choice field to its exclusive value (selecting it clears the
// others; selecting any other value clears it). Read by the machine's
// `toggleMultiChoice` action.
export const MULTI_CHOICE_FIELDS: Partial<
  Record<FhsField, {exclusiveValue?: string}>
> = {
  insurance: {exclusiveValue: 'NONE'},
  estatePlanning: {exclusiveValue: 'NONE'},
};
